import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import { api } from '../api/client'
import type { AwxConfronto, AwxInventario } from '../api/types'
import {
  Card,
  EmptyState,
  ErrorBanner,
  PageSection,
  SectionedPage,
  Spinner,
  StatCard,
  formatDateTime,
} from '../components/common'
import { Connessioni } from '../components/Connessioni'
import { DataTable } from '../components/DataTable'
import { useAsync } from '../hooks/useAsync'
import { t } from '../i18n/lingua'

/**
 * Ansible AWX, a section of its own (it used to be a panel on the card in
 * Sorgenti dati). Three things: the **comparison** — which hosts of the CMDB
 * are not in AWX (no playbook will touch them), which AWX hosts CENSO does not
 * know, which are matched — read live from AWX on request; the **dynamic
 * inventory**, AWX taking its hosts from the CMDB; the **connections**, the AWX
 * credentials with the same form as everywhere else.
 */
export default function Awx() {
  const servers = useAsync(() => api.awxServers(), [])
  const license = useAsync(() => api.license(), [])
  const licenziato = license.data?.features?.awx !== false
  const elenco = servers.data ?? []

  return (
    <>
      <header className="page__header">
        <div>
          <h1 className="page__title">{t('Ansible AWX')}</h1>
          <p className="page__subtitle">
            {t('AWX / Automation Controller confronta la CMDB con gli host gestiti da AWX ed evidenzia i CI non automatizzati, gli host non censiti e l’inventario dinamico che sincronizza gli host dalla piattaforma ad AWX.')}
          </p>
        </div>
      </header>
      {!licenziato && (
        <div className="banner banner--warn">{t('Non compreso nella licenza')}</div>
      )}

      <SectionedPage
        sections={[
          { id: 'confronto', label: t('Confronto') },
          { id: 'inventario', label: t('Inventario dinamico') },
          { id: 'connessioni', label: t('Connessioni') },
        ]}
      >
        <PageSection id="confronto">
          <ErrorBanner error={servers.error} />
          {servers.loading && !servers.data ? (
            <Spinner />
          ) : elenco.length === 0 ? (
            <EmptyState
              title={t('Nessun AWX collegato')}
              hint={t('Aggiungi una connessione AWX (token, da una sonda o dal Control Plane) nella sezione Connessioni: il confronto legge gli host di AWX in tempo reale.')}
            />
          ) : (
            <Confronto servers={elenco} />
          )}
        </PageSection>

        <PageSection id="inventario">
          <Inventario />
        </PageSection>

        <PageSection id="connessioni">
          <Connessioni funzioni={['awx']} segnaposto="awx-produzione" onChanged={() => servers.reload()} />
        </PageSection>
      </SectionedPage>
    </>
  )
}

function Confronto({ servers }: { servers: { id: string; name: string; inventory: string }[] }) {
  const [server, setServer] = useState('')
  const [inventario, setInventario] = useState('')
  const [inventari, setInventari] = useState<AwxInventario[] | null>(null)
  const [filtri, setFiltri] = useState({ classes: '', tags: '' })
  const [esito, setEsito] = useState<AwxConfronto | null>(null)
  const [lavoro, setLavoro] = useState(false)
  const [errore, setErrore] = useState<unknown>(null)
  const scelta = server || servers[0]?.id || ''

  useEffect(() => {
    if (!scelta) return
    let vivo = true
    setInventari(null)
    void api.awxInventories(scelta)
      .then((r) => { if (vivo) setInventari(r.available && r.inventories ? r.inventories : []) })
      .catch(() => { if (vivo) setInventari([]) })
    return () => { vivo = false }
  }, [scelta])

  async function confronta() {
    setLavoro(true)
    setErrore(null)
    try {
      setEsito(await api.awxConfronta({ server: scelta, inventory: inventario, classes: filtri.classes, tags: filtri.tags }))
    } catch (e) {
      setErrore(e)
    } finally {
      setLavoro(false)
    }
  }

  const mancanti = esito?.missing_in_awx ?? []
  const sconosciuti = esito?.unknown_in_censo ?? []
  const abbinati = esito?.aligned ?? []
  return (
    <>
      <Card title={t('Confronto con gli host di AWX')}>
        <p className="muted" style={{ marginTop: 0 }}>
          {t('Gli host si abbinano per nome, nome breve o ansible_host. Classi e tag restringono la CMDB a ciò che AWX dovrebbe avere.')}
        </p>
        <div className="awx__riga">
          {servers.length > 1 && (
            <select aria-label={t('AWX')} value={scelta} onChange={(e) => { setServer(e.target.value); setInventario(''); setEsito(null) }}>
              {servers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          )}
          <select aria-label={t('Inventario di AWX')} value={inventario} onChange={(e) => setInventario(e.target.value)} disabled={inventari === null}>
            <option value="">{inventari === null ? t('leggo gli inventari…') : t('inventario della credenziale (o tutti)')}</option>
            {(inventari ?? []).map((i) => <option key={i.id} value={String(i.id)}>{i.name}{i.hosts != null ? ` (${i.hosts})` : ''}</option>)}
          </select>
          <input type="text" aria-label={t('Classi')} placeholder={t('classi: server,switch')} value={filtri.classes} onChange={(e) => setFiltri({ ...filtri, classes: e.target.value })} />
          <input type="text" aria-label={t('Tag')} placeholder={t('tag: ruolo:web')} value={filtri.tags} onChange={(e) => setFiltri({ ...filtri, tags: e.target.value })} />
          <button type="button" onClick={() => void confronta()} disabled={lavoro}>{lavoro ? t('Confronto…') : t('Confronta')}</button>
        </div>
        <ErrorBanner error={errore} />
        {esito && !esito.available && <p className="field__hint" style={{ color: 'var(--warn)' }}>{esito.reason}</p>}
        {esito?.available && (
          <>
            <div className="stats">
              <StatCard label={t('Host in CENSO')} value={esito.censo_hosts ?? 0} />
              <StatCard label={t('Host in AWX')} value={esito.awx_hosts ?? 0} />
              <StatCard label={t('Abbinati')} value={esito.matched ?? 0} tone="ok" />
              <StatCard label={t('In CENSO, non in AWX')} value={mancanti.length} tone={mancanti.length ? 'warn' : 'ok'} />
              <StatCard label={t('In AWX, sconosciuti a CENSO')} value={sconosciuti.length} tone={sconosciuti.length ? 'warn' : 'ok'} />
            </div>
            <p className="muted" style={{ margin: '6px 0 0' }}>
              {t('Inventario: {inv} · letto alle {ora}', {
                inv: esito.inventory ? esito.inventory.name : t('tutti gli host'),
                ora: formatDateTime(esito.generated_at ?? null),
              })}
            </p>
          </>
        )}
      </Card>

      {esito?.available && (
        <>
          <Card title={t('In CENSO, non in AWX ({n})', { n: mancanti.length })}>
            {mancanti.length === 0 ? <p className="muted">{t('Nessuno: tutti gli host della CMDB sono in AWX.')}</p> : (
              <DataTable rows={mancanti} rowKey={(r) => r.ci_id} conteggio={{ nome: t('host') }} columns={[
                { key: 'name', header: t('Host'), value: (r) => r.name, render: (r) => <Link to={`/cis/${r.ci_id}`}>{r.name}</Link> },
                { key: 'ip', header: t('Indirizzo'), mono: true, value: (r) => r.ip ?? '' },
                { key: 'class', header: t('Classe'), value: (r) => r.class ?? '' },
                { key: 'tags', header: t('Tag'), value: (r) => Object.entries(r.tags).map(([k, v]) => (v ? `${k}:${v}` : k)).join(', ') },
              ]} />
            )}
          </Card>
          <Card title={t('In AWX, sconosciuti a CENSO ({n})', { n: sconosciuti.length })}>
            {sconosciuti.length === 0 ? <p className="muted">{t('Nessuno.')}</p> : (
              <DataTable rows={sconosciuti} rowKey={(r) => `${r.inventory}|${r.name}`} conteggio={{ nome: t('host') }} columns={[
                { key: 'name', header: t('Host in AWX'), value: (r) => r.name },
                { key: 'ansible_host', header: t('ansible_host'), mono: true, value: (r) => r.ansible_host },
                { key: 'enabled', header: t('Abilitato'), value: (r) => (r.enabled ? t('sì') : t('no')) },
              ]} />
            )}
          </Card>
          <Card title={t('Abbinati ({n})', { n: abbinati.length })}>
            {abbinati.length === 0 ? <p className="muted">{t('Nessuno.')}</p> : (
              <DataTable rows={abbinati} rowKey={(r) => r.ci_id} conteggio={{ nome: t('host') }} columns={[
                { key: 'name', header: t('Host in CENSO'), value: (r) => r.name, render: (r) => <Link to={`/cis/${r.ci_id}`}>{r.name}</Link> },
                { key: 'awx_name', header: t('Host in AWX'), value: (r) => r.awx_name },
                { key: 'ip', header: t('Indirizzo'), mono: true, value: (r) => r.ip ?? '' },
                { key: 'awx_enabled', header: t('Abilitato in AWX'), value: (r) => (r.awx_enabled ? t('sì') : t('no')) },
              ]} />
            )}
          </Card>
        </>
      )}
    </>
  )
}

/** AWX ← CENSO: the URL of the inventory, the script for the project, the filters. */
function Inventario() {
  const origine = window.location.origin
  const [filtri, setFiltri] = useState({ classes: '', tags: '' })
  const q = new URLSearchParams()
  if (filtri.classes.trim()) q.set('classes', filtri.classes.trim())
  if (filtri.tags.trim()) q.set('tags', filtri.tags.trim())
  const url = `${origine}/api/v1/awx/inventory${q.toString() ? `?${q}` : ''}`
  return (
    <Card title={t('Inventario dinamico (AWX ← CENSO)')}>
      <p className="muted" style={{ marginTop: 0 }}>
        {t('AWX / Automation Controller utilizza la CMDB come sorgente dinamica d’inventario tramite una chiave API in sola lettura; i tag vengono esposti come gruppi, consentendo di confrontare gli host gestiti da AWX con quelli censiti e individuare eventuali disallineamenti.')}
      </p>
      <ol className="awx__passi">
        <li>{t('Crea una chiave API con permessi di lettura per l’integrazione Ansible AWX nella sezione Amministrazione → Chiavi API.')}</li>
        <li>{t('Scarica lo script dell’inventario dinamico e pubblicalo nel repository Git utilizzato dal progetto AWX.')}</li>
        <li>{t('Configura in AWX una sorgente d’inventario «Sourced from a Project», associandola allo script e impostando l’indirizzo della piattaforma e la chiave API nelle variabili d’ambiente.')}</li>
        <li>{t('Sincronizza l’inventario: gli host vengono importati automaticamente dalla CMDB e i tag vengono esposti come gruppi AWX.')}</li>
      </ol>
      <div className="awx__riga">
        <input type="text" aria-label={t('Classi')} placeholder={t('classi: server,switch')} value={filtri.classes} onChange={(e) => setFiltri({ ...filtri, classes: e.target.value })} />
        <input type="text" aria-label={t('Tag')} placeholder={t('tag: ruolo:web')} value={filtri.tags} onChange={(e) => setFiltri({ ...filtri, tags: e.target.value })} />
      </div>
      <div className="awx__riga">
        <code className="mono">{url}</code>
        <a className="secondary button" href={`${origine}/api/v1/awx/inventory/script`} target="_blank" rel="noreferrer">{t('Scarica lo script')}</a>
        <Link to="/guide#awx" className="muted">{t('come si configura')}</Link>
      </div>
      <h4 className="awx__titolo">{t('Variabili d’ambiente della sorgente in AWX')}</h4>
      <pre className="awx__env mono">{[
        `CENSO_URL=${origine}`,
        `CENSO_API_KEY=censo_tk_…`,
        ...(q.toString() ? [`CENSO_INVENTORY_QUERY=${q}`] : []),
      ].join('\n')}</pre>
    </Card>
  )
}
